import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Table, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { toast } from "react-hot-toast";
import MainLayout from "../../Layout/mainLayout";
import {
  listProjectsWithSlabForFranchise,
  addCommissionSlab,
  updateCommissionSlab,
} from "../../Redux/Actions/commissionSlabAction";
import CommissionSlabModal from "../Manage Projects/commissionSlabModal";

const formatSlab = (slab) => {
  if (!slab) return "Not Set";
  const unit = slab.amount_type === "percentage" ? "%" : slab.amount_type === "per_sqft" ? "/sqft" : "";
  return `${slab.base_amount}${unit} — Admin ${slab.split?.admin} / FM ${slab.split?.franchise_manager}`;
};

const emptyCommissionForm = {
  amount_type: "percentage",
  base_amount: "",
  is_gst_excluded: false,
  gst_percentage: 18,
  split: { type_of_split: "percentage", admin: "", franchise_manager: "" },
};

const ListFranchiseProjects = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { franchise_id } = useParams();

  const { projectsWithSlabList, loading } = useSelector((state) => state.commissionSlab || {});
  const { admin } = useSelector((state) => state.admin || {});
  const accessCode = admin?.data?.access_code || [];
  const canAdd = Array.isArray(accessCode) && accessCode.includes("add");

  // ── Search state ──
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState({ current: 1, pageSize: 10 });
  const debounceRef = useRef(null);

  // ── Commission popup state ──
  const [commissionPopup, setCommissionPopup] = useState(false);
  const [activeRecord, setActiveRecord] = useState(null); // holds { project, property, commission_slab }
  const [commissionSaving, setCommissionSaving] = useState(false);
  const [commissionForm, setCommissionForm] = useState(emptyCommissionForm);

  const fetchList = useCallback(
    (term) => {
      if (franchise_id) {
        dispatch(listProjectsWithSlabForFranchise(franchise_id, term));
      }
    },
    [franchise_id, dispatch]
  );

  // initial load
  useEffect(() => {
    fetchList("");
  }, [fetchList]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setPage((p) => ({ ...p, current: 1 }));

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchList(value);
    }, 400);
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const dataSource = Array.isArray(projectsWithSlabList?.data) ? projectsWithSlabList.data : [];

  const tableData = dataSource.flatMap((entry, pIdx) => {
    const rows = entry.properties?.length
      ? entry.properties
      : [{ property: null, commission_slab: null }];
    return rows.map((item, i) => ({
      key: `${entry.project?._id}-${item.property?._id || "none"}`,
      project: entry.project,
      property: item.property,
      commission_slab: item.commission_slab,
      projectIndex: pIdx,
      rowSpan: i === 0 ? rows.length : 0,
    }));
  });

  const pagedData = tableData
    .slice((page.current - 1) * page.pageSize, page.current * page.pageSize)
    .map((row, i, arr) => {
      const isFirst = i === 0 || arr[i - 1].project?._id !== row.project?._id;
      return {
        ...row,
        rowSpan: isFirst ? arr.filter((r) => r.project?._id === row.project?._id).length : 0,
      };
    });

  const openCommissionPopup = (record) => {
    const slab = record.commission_slab;
    setActiveRecord({ project: record.project, property: record.property, commission_slab: slab });
    setCommissionForm(
      slab
        ? {
          amount_type: slab.amount_type || "percentage",
          base_amount: slab.base_amount ?? "",
          is_gst_excluded: slab.is_gst_excluded || false,
          gst_percentage: slab.gst_percentage ?? 18,
          split: {
            type_of_split: slab.split?.type_of_split || "percentage",
            admin: slab.split?.admin ?? "",
            franchise_manager: slab.split?.franchise_manager ?? "",
          },
        }
        : emptyCommissionForm
    );
    setCommissionPopup(true);
  };

  const closeCommissionPopup = () => {
    setCommissionPopup(false);
    setActiveRecord(null);
    setCommissionForm(emptyCommissionForm);
  };

  const handleSaveCommissionSlab = async () => {
    if (!activeRecord?.project?._id || !activeRecord?.property?._id) return;

    const { amount_type, base_amount, split, is_gst_excluded, gst_percentage } = commissionForm;

    if (!amount_type) return toast.error("Select an amount type");
    if (!base_amount || Number(base_amount) <= 0) return toast.error("Enter a valid amount");
    if (!split.type_of_split) return toast.error("Select a split type");

    if (is_gst_excluded && (Number(gst_percentage) < 0 || Number(gst_percentage) > 100)) {
      return toast.error("GST percentage must be between 0 and 100");
    }

    const adminVal = Number(split.admin);
    const fmVal = Number(split.franchise_manager);
    const expectedTotal = Number(base_amount);

    if (adminVal + fmVal !== expectedTotal) {
      return toast.error(`Admin + Franchise Manager must total ${expectedTotal}`);
    }

    const payload = {
      project_id: activeRecord.project._id,
      properties_id: activeRecord.property._id,
      amount_type,
      base_amount: Number(base_amount),
      is_gst_excluded,
      gst_percentage: Number(gst_percentage),
      split: { type_of_split: split.type_of_split, admin: adminVal, franchise_manager: fmVal },
    };

    const existingId = activeRecord.commission_slab?._id;

    try {
      setCommissionSaving(true);
      const result = existingId
        ? await dispatch(updateCommissionSlab(existingId, payload))
        : await dispatch(addCommissionSlab(payload));

      if (result?.statusCode === 200) {
        toast.success(existingId ? "Commission slab updated!" : "Commission slab added!");
        closeCommissionPopup();
        fetchList(searchTerm);
      }
    } finally {
      setCommissionSaving(false);
    }
  };


  const spanCell = (record) => ({ rowSpan: record.rowSpan });

  const columns = [
    {
      title: "Sr No.",
      align: "center",
      onCell: spanCell,
      render: (_, record) => record.projectIndex + 1,
    },
    {
      title: "Cover",
      align: "center",
      onCell: spanCell,
      render: (_, record) =>
        record.project?.cover_photo ? (
          <img
            src={record.project.cover_photo}
            alt="Project"
            style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6 }}
          />
        ) : (
          "—"
        ),
    },
    {
      title: "Project Name",
      align: "center",
      onCell: spanCell,
      render: (_, record) => record.project?.project_name || "—",
    },
    {
      title: "Brand",
      align: "center",
      onCell: spanCell,
      render: (_, record) => record.project?.brand_name || "—",
    },
    {
      title: "Status",
      align: "center",
      onCell: spanCell,
      render: (_, record) => record.project?.status || "—",
    },
    {
      title: "Property",
      align: "center",
      render: (_, record) =>
        record.property?.property_name || record.property?.property_id || "—",
    },
    {
      title: "Category",
      align: "center",
      render: (_, record) => record.property?.category?.name || "—",
    },
    {
      title: "Commission Slab",
      align: "center",
      render: (_, record) => (record.property ? formatSlab(record.commission_slab) : "—"),
    },
    {
      title: "Action",
      align: "center",
      render: (_, record) =>
        canAdd && record.property ? (
          <button
            onClick={() => openCommissionPopup(record)}
            style={{
              padding: "6px 14px",
              border: "1px solid #8b5cf6",
              background: "white",
              color: "#6b46c1",
              borderRadius: "8px",
              fontSize: "13px",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {record.commission_slab ? "Update Commission" : "Add Commission"}
          </button>
        ) : (
          "—"
        ),
    },
  ];

  return (
    <MainLayout>
      <div className="vfm-wrapper">
        <div className="vfm-header">
          <div className="vfm-header-left">
            <button
              className="vfm-back-btn"
              onClick={() => navigate(location.state?.from || "/franchise")}
              type="button"
              aria-label="Go back"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <div>
              <h1 className="vfm-title">Projects</h1>
              <p className="vfm-subtitle">Projects assigned to this franchise manager, with commission slab</p>
            </div>
          </div>

          <div className="vfm-header-right">
            <Input
              placeholder="Search by project or brand name"
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={handleSearchChange}
              allowClear
              style={{ width: 280 }}
            />
          </div>
        </div>

        <div className="vfm-section-card">
          <Table
            dataSource={pagedData}
            columns={columns}
            loading={loading}
            rowKey="key"
            pagination={{
              current: page.current,
              pageSize: page.pageSize,
              total: tableData.length,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              onChange: (current, pageSize) => setPage({ current, pageSize }),
            }}
            scroll={{ x: 1200 }}
            bordered
          />
        </div>
      </div>

      {commissionPopup && (
        <CommissionSlabModal
          onClose={closeCommissionPopup}
          onSave={handleSaveCommissionSlab}
          formData={commissionForm}
          setFormData={setCommissionForm}
          loading={commissionSaving}
          canAdd={canAdd}
          isEdit={!!activeRecord?.commission_slab?._id}
        />
      )}
    </MainLayout>
  );
};

export default ListFranchiseProjects;